/**
 * Utility functions for converting weekly schedule between frontend and backend formats
 * 
 * Backend Format:
 * - Open/close times in 24-hour format
 * - Commas separate open/close pairs: "09:00,13:00"
 * - Pipe separates multiple periods: "09:00,13:00|14:00,18:00"
 * - Use "00:00,00:01" for fully closed days
 * 
 * Frontend Format:
 * - JSON array of DaySchedule objects with 12-hour time format
 */

export interface TimeRange {
    id: string;
    startTime: string; // 12-hour format: "9:00 AM"
    endTime: string;   // 12-hour format: "5:00 PM"
}

export interface DaySchedule {
    day: string;
    isOpen: boolean;
    timeRanges: TimeRange[];
}

/**
 * Convert 12-hour time to 24-hour format
 * Example: "1:00 PM" -> "13:00"
 */
export const convert12HourTo24Hour = (time12: string): string => {
    const [time, period] = time12.trim().split(' ');
    const [hourStr, minute] = time.split(':');
    let hour = parseInt(hourStr, 10);

    if (period === 'PM' && hour !== 12) {
        hour += 12;
    } else if (period === 'AM' && hour === 12) {
        hour = 0;
    }

    return `${hour.toString().padStart(2, '0')}:${minute}`;
};

/**
 * Convert 24-hour time to 12-hour format
 * Example: "13:00" -> "1:00 PM"
 */
export const convert24HourTo12Hour = (time24: string): string => {
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? 'PM' : 'AM';

    if (hour === 0) {
        hour = 12;
    } else if (hour > 12) {
        hour -= 12;
    }

    return `${hour}:${minute} ${period}`;
};

/**
 * Convert a single day's schedule to backend format
 * Examples:
 * - Closed: "00:00,00:01"
 * - Single period: "09:00,17:00"
 * - Multiple periods: "09:00,12:00|14:00,18:00"
 */
export const dayScheduleToBackendFormat = (daySchedule: DaySchedule): string => {
    // Closed day
    if (!daySchedule.isOpen || daySchedule.timeRanges.length === 0) {
        return '00:00,00:01';
    }

    // Convert each time range to 24-hour format and join with commas
    const periods = daySchedule.timeRanges.map(tr => {
        const start24 = convert12HourTo24Hour(tr.startTime);
        const end24 = convert12HourTo24Hour(tr.endTime);
        return `${start24},${end24}`;
    });

    // Join multiple periods with pipe
    return periods.join('|');
};

/**
 * Parse backend format for a single day to DaySchedule
 * Examples:
 * - "00:00,00:01" -> Closed
 * - "09:00,17:00" -> Open 9am-5pm
 * - "09:00,12:00|14:00,18:00" -> Open 9am-12pm and 2pm-6pm
 */
export const backendFormatToDaySchedule = (day: string, backendStr: string): DaySchedule => {
    // Check if closed
    if (backendStr === '00:00,00:01') {
        return {
            day,
            isOpen: false,
            timeRanges: []
        };
    }

    // Split by pipe for multiple periods
    const periods = backendStr.split('|');
    const timeRanges: TimeRange[] = periods.map((period, index) => {
        const [start, end] = period.split(',');
        return {
            id: crypto.randomUUID(),
            startTime: convert24HourTo12Hour(start.trim()),
            endTime: convert24HourTo12Hour(end.trim())
        };
    });

    return {
        day,
        isOpen: true,
        timeRanges
    };
};

/**
 * Convert weekly schedule to backend format
 * Returns an array of 7 strings (Monday through Sunday)
 */
export const weeklyScheduleToBackend = (schedule: DaySchedule[]): string[] => {
    const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return DAYS_ORDER.map(day => {
        const daySchedule = schedule.find(d => d.day === day);
        if (!daySchedule) {
            return '00:00,00:01'; // Default to closed if day not found
        }
        return dayScheduleToBackendFormat(daySchedule);
    });
};

/**
 * Parse backend format array to weekly schedule
 * Expects array of 7 strings (Monday through Sunday)
 */
export const backendToWeeklySchedule = (backendArray: string[]): DaySchedule[] => {
    const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    if (!backendArray || backendArray.length !== 7) {
        // Return default closed schedule if invalid
        return DAYS_ORDER.map(day => ({
            day,
            isOpen: false,
            timeRanges: []
        }));
    }

    return DAYS_ORDER.map((day, index) => {
        return backendFormatToDaySchedule(day, backendArray[index]);
    });
};

/**
 * Convert frontend JSON string to backend format
 */
export const convertWeeklyScheduleToBackend = (jsonStr: string): string[] => {
    try {
        const schedule: DaySchedule[] = JSON.parse(jsonStr);
        return weeklyScheduleToBackend(schedule);
    } catch {
        // Return default closed schedule if parsing fails
        return Array(7).fill('00:00,00:01');
    }
};

/**
 * Convert backend format to frontend JSON string
 */
export const convertWeeklyScheduleFromBackend = (backendArray: string[]): string => {
    const schedule = backendToWeeklySchedule(backendArray);
    return JSON.stringify(schedule);
};

/**
 * Validate backend format for a single day
 */
export const isValidDayFormat = (str: string): boolean => {
    if (!str) return false;

    // Check for closed format
    if (str === '00:00,00:01') return true;

    // Check for time periods format
    const periodPattern = /^\d{2}:\d{2},\d{2}:\d{2}$/;
    const periods = str.split('|');

    return periods.every(period => periodPattern.test(period.trim()));
};
